import React from "react"
import * as Yup from 'yup';
import { Grid } from '@mui/material'
import { Container } from './styled';
import { useRouter } from "next/router";
import { LoadingBuffer } from "../utils/Loading";
import { yupResolver } from "@hookform/resolvers/yup";
import { DeleteElaborationDialog } from "../../Modal/Delete";
import { TextFieldElaboration } from "../../Inputs/TextField";
import { ActionButtonElaborations } from "../../ActionButton";
import { Organograma } from "@/utils/organograma/Organograma";
import { useTipeOfFlow } from "@/services/zustand/tipo_fluxo";
import { RadioCheckedElaboration } from "../../Inputs/RadioButton";
import { ElaborationService } from "@/services/endpoints/elaboracao";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useSnackbarElaboration } from "@/hooks/useSnackbarElaboration";
import { ReutilizarElaborationDialogV2 } from "../Vision/ReutilizarModal";
import { findingMenuItemKey } from "@/contexts/elaboracao_planos/constants";
import { addItemToNodeOrganogram } from "@/utils/organograma/addItemToNode";
import { RelacionamentoJsonService } from "@/services/endpoints/hierarquia";
import { DisclaimerReuseElaborationDialog } from "../Vision/disclaimerModal";
import { DeleteIcon, transformGuidelineAspirationStrategyV2 } from "../utils";
import { useElaborationCardItem } from "@/services/zustand/elaboration_card_item";
import { useHierarchyPreparationOfPlans, usePreparationOfPlans } from "@/hooks/elaboracao_planos";

function validateArray(array: any) {
  if (!Array.isArray(array)) {
    throw new TypeError('O parâmetro deve ser um array.');
  }

  for (let i = 0; i < array.length; i++) {
    const innerArray = array[i];

    const hasFilledItem = innerArray.some((item: any) => {
      return item.label !== null &&
        item.eixo_tematico_id !== null &&
        item.texto !== null &&
        item.data !== null
    });

    if (hasFilledItem) {
      return { error: null };
    }
  }

  return { error: 'Pelo menos um dos campos deve ter itens preenchidos.', index: [0, 0] };
}

type InputsGuidelines = {
  diretriz: any;
  is_optional?: boolean;
};

const SchemaGuidelines = Yup.object().shape({
  diretriz: Yup.array().required('Campo obrigatório')
});

const defaultValue = {
  id: '',
  texto: '',
  label: '',
  data: null,
}

export const GuidelinesForm: React.FC = () => {
  const {
    watch: watchGuidelines,
    control: controlGuidelines,
    register: registerGuidelines,
    setError: setErrorGuidelines,
    setValue: setValueGuidelines,
    handleSubmit: handleSubmitGuidelines,
    formState: { errors: errosGuidelines } } = useForm<InputsGuidelines>({
      resolver: yupResolver(SchemaGuidelines),
      defaultValues: { diretriz: [[defaultValue]] }
    });

  const { update: updateGuidelines } = useFieldArray({
    control: controlGuidelines,
    name: "diretriz"
  });

  const fields = watchGuidelines('diretriz');
  const is_optional = watchGuidelines('is_optional') as unknown as string;

  const router = useRouter();
  const pathname = router.pathname;
  const [data, setData] = React.useState<any[]>([]);
  const replaceButtonRef = React.useRef(null) as any;
  const [loading, setLoading] = React.useState(false);
  const { tipo: tipoFluxo, planId } = useTipeOfFlow();
  const [disable, setDisable] = React.useState(false);
  const [deleteField, setDeleteField] = React.useState([]);
  const [reutilizar, setReutilizar] = React.useState(false);
  const [deleteFieldId, setDeleteFieldId] = React.useState(0);
  const [fieldIndexItem, setFieldIndexItem] = React.useState(0);
  const [disabeSubmit, setDisableSubmit] = React.useState(false);
  const [openDisclaimer, setOpenDisclaimer] = React.useState(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [deleteStrategyId, setDeleteStrategyId] = React.useState(null);
  const [reuseClickedIndex, setReuseClickedIndex] = React.useState(null);
  const { toggleErrorSnackbar, toggleSuccessSnackbar } = useSnackbarElaboration()
  const zustandSelectedCardItem = useElaborationCardItem(state => state.cardItem);
  const { GuideLinesElaboration, GetThematicAxisElaboration, GetGuideLinesElaboration, DeleteGuideLinesElaborations, PostProgressCode } = React.useMemo(() => new ElaborationService(), []);
  const { itemsMenu, selectedItemsMenu, completCode, handleFetchProgressCodePlanId, setNavigable, setDefaultItemMenuId } = usePreparationOfPlans();

  const handleAddLine = (field: any, it: any, fieldIndex: any) => {
    const newPlanejados = [...field, { texto: '', data: null, eixo_tematico_id: it.eixo_tematico_id }];
    updateGuidelines(fieldIndex, newPlanejados);
  };

  React.useEffect(() => {
    const id = router.query.id;
    if (!id) return;
    if (!(pathname.includes('/elaboracao_planos') || pathname.includes('/aprovacao_planos'))) return;

    setLoading(true)
    const fetchData = async () => {
      const { data: axisData } = await GetThematicAxisElaboration(id);
      const axisLabel = axisData.map((item: any) => ({
        label: item.texto,
        eixo_tematico_id: item.id,
      }));

      const { data } = await GetGuideLinesElaboration(id);
      const parsedData = data.filter((item: any) => item?.eixoTematico);

      const transformedArray = transformGuidelineAspirationStrategyV2(parsedData, axisLabel);

      if (transformedArray.length > 0) {
        setValueGuidelines('diretriz', transformedArray);
      }
    };

    fetchData()
      .catch(() => {
      })
      .finally(() => setLoading(false));
  }, [GetGuideLinesElaboration, GetThematicAxisElaboration, pathname, router.query.id, setValueGuidelines]);

  React.useEffect(() => {
    if (!planId) return;
    if (tipoFluxo != 'substituir') return;
    if (!(pathname.includes('/elaboracao_planos') || pathname.includes('/aprovacao_planos'))) return;

    setLoading(true);
    if (planId && (pathname.includes('/elaboracao_planos') || pathname.includes('/aprovacao_planos')) && tipoFluxo == 'substituir') {
      const fetchData = async () => {
        try {
          const { data } = await GetGuideLinesElaboration(planId);
          const parsedData = data.filter((item: any) => item?.eixoTematico);

          if (parsedData.length > 0) {
            const transformedArray = parsedData.map((item: any) => ({ data: item.data, texto: item.texto }))

            setData(transformedArray);
          }

        } catch (error) {
          console.error('Failed to fetch aspiration elaboration data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [GetGuideLinesElaboration, pathname, planId, tipoFluxo]);

  React.useEffect(() => {
    const code = completCode?.filter(item => item?.code == 'diretrizes');
    if (code.length > 0) {
      setDisable(true)
    } else {
      setDisable(false)
    }
  }, [completCode]);

  React.useEffect(() => {
    const code = completCode.find(item => item.code == 'diretrizes' && item.skipped == false);

    if (code != undefined) {
      setValueGuidelines("is_optional", true)
    }
  }, [completCode, setValueGuidelines])

  const { consultarOrganograma, salvarOrganograma, startOrganograma } = new Organograma(router.query.id as unknown as number);

  const { insertItemById, findNewItemsWithId, updateItemById, deleteItemById } = useHierarchyPreparationOfPlans();
  const { startRelacionamento, consultarRelacionamento, atualizarRelacionamento } = new RelacionamentoJsonService(router.query.id as unknown as number);

  function hasDiretrizes(eixosTematicos: any, eixoId: any) {
    if (!eixosTematicos || !eixosTematicos.itens) {
      throw new Error("Estrutura de eixos temáticos inválida.");
    }

    const eixo = eixosTematicos.itens.find((item: any) => item.id === eixoId);

    if (!eixo) {
      return false; // Eixo temático com o ID fornecido não foi encontrado
    }

    // Verifica se o relacionamento possui a camada estratégias
    return eixo.relacionamento?.diretrizes?.itens?.length > 0;
  }


  const onSubmit: SubmitHandler<any> = async (data, event: any) => {
    const { diretriz } = data;
    const diretrizCode = completCode.find(item => item.code == 'diretrizes')

    if (disabeSubmit) return;

    setDisableSubmit(true);

    try {
      const { error } = validateArray(diretriz);

      if (error !== null) {
        setErrorGuidelines('root', { message: error as any });
        setDisableSubmit(false);
        return;
      }

      const filteredArrays = diretriz
        .map((subArray: any) =>
          subArray.filter((item: any) => !(item.texto === null || (item.texto === "" && item.data === null)))
        )
        .filter((subArray: any) => subArray.length > 0)
        .flat();

      const newItem = filteredArrays.map((item: any) => {
        const { id, label, plano, ...res } = item;

        if (id == null || id === '') {
          return {
            ...res,
          };
        } else {
          return {
            ...res,
            id,
          };
        }
      });

      const res = await GuideLinesElaboration({
        id: Number(router.query.id),
        data: { itens: newItem },
      });

      const response = res.data as any[];
      const newArray = findNewItemsWithId(newItem, response);

      for (const item of newArray) {
        const relacionamento = await consultarRelacionamento();
        const root = JSON.parse(JSON.parse(relacionamento));

        const checkId = (item: any) => {
          if (item.eixo_tematico_id != undefined) {
            return item.eixo_tematico_id;
          }

          if (item.eixoTematico != undefined) {
            return item.eixoTematico
          }
        }

        if (!hasDiretrizes(root.eixos_tematicos, checkId(item))) {
          try {
            const relacionamento = await consultarRelacionamento();
            const root = JSON.parse(JSON.parse(relacionamento));
            const newInsertItem = updateItemById({
              root,
              targetId: item.eixoTematico || item.eixo_tematico_id,
              item: { diretrizes: { itens: [] } },
              layerName: 'eixos_tematicos',
            });

            startRelacionamento(JSON.stringify(newInsertItem));
            await atualizarRelacionamento(); // Aguarda o término da atualização

            const relacionamentov2 = await consultarRelacionamento();
            const rootv2 = JSON.parse(JSON.parse(relacionamentov2));

            const newInsertItemv2 = insertItemById({
              root: rootv2,
              targetId: item.eixoTematico || item.eixo_tematico_id,
              item: { id: item.id, relacionamento: {} },
              layerName: 'diretrizes',
            });

            startRelacionamento(JSON.stringify(newInsertItemv2));
            await atualizarRelacionamento(); // Aguarda o término da atualização
          } catch (error) {
            console.error(`Erro ao atualizar a camada de eixos temáticos para o item ${item.eixoTematico}:`, error);
          }
        } else {
          try {
            const relacionamento = await consultarRelacionamento();
            const root = JSON.parse(JSON.parse(relacionamento));

            const newInsertItem = insertItemById({
              root,
              targetId: item.eixoTematico || item.eixo_tematico_id,
              item: { id: item.id, relacionamento: {} },
              layerName: 'diretrizes',
            });

            startRelacionamento(JSON.stringify(newInsertItem));
            await atualizarRelacionamento(); // Aguarda o término da atualização
          } catch (error) {
            console.error(`Erro ao inserir o item na camada de diretrizes para o eixo temático ${item.eixoTematico}:`, error);
          }
        }
      }

      toggleSuccessSnackbar()
      setTimeout(() => toggleSuccessSnackbar(), 3000)
    } catch (error) {
      toggleErrorSnackbar();
    } finally {
      if (event.nativeEvent.submitter.name === 'next_step') {
        setNavigable(true);

        if (router.pathname.includes('epl')) {
          router.push('dashboard/aprovacao_planos/' + router.query.id + '/epl/preview')
        } else {
          router.push('/dashboard/elaboracao_planos/' + router.query.id);
        }

        const plan = itemsMenu[findingMenuItemKey(zustandSelectedCardItem)] as Record<string, any>;
        const planId =
          plan[selectedItemsMenu?.layer as string].find((item: any) => item.id == selectedItemsMenu?.id).id + 1;
        setDefaultItemMenuId(planId);
        handleFetchProgressCodePlanId(router.query.id);
      } else {
        setNavigable(false);
        const plan = itemsMenu[findingMenuItemKey(zustandSelectedCardItem)] as Record<string, any>;
        const planId =
          plan[selectedItemsMenu?.layer as string].find((item: any) => item.id == selectedItemsMenu?.id).id;
        setDefaultItemMenuId(planId);

        if (router.pathname.includes('epl')) {
          router.push('dashboard/aprovacao_planos/' + router.query.id + '/epl/preview')
        } else {
          router.push('/dashboard/elaboracao_planos/' + router.query.id);
        }
        handleFetchProgressCodePlanId(router.query.id);
      }

      setTimeout(() => setDisableSubmit(false), 3000);
    }

    if (diretrizCode != undefined) return;

    try {
      const response = await consultarOrganograma();
      const organograma = response.Organograma;

      const newItem = {
        "tooltip": "",
        "position": "",
        "manager": 'Eixo Temático',
        "name": "Diretriz",
        "subordinates": [],
      };

      const newOrganograma = addItemToNodeOrganogram(organograma, 'Eixo Temático', newItem);
      startOrganograma(newOrganograma);
      salvarOrganograma();
    } catch {
    }
  };

  const Fields = () => {
    if (Boolean(is_optional) == false || is_optional == undefined) return null;
    if (is_optional == 'false' || is_optional == undefined) return null;

    const inputs = fields?.reduce((acc: any, field: any, fieldIndex: any) => {
      field.map((it: any, itIndex: any) => {
        const texto = watchGuidelines(`diretriz.${fieldIndex}.${itIndex}.texto`);

        acc.push(
          <React.Fragment key={it?.id || `${fieldIndex}-${itIndex}`}>
            <input
              type="hidden"
              {...registerGuidelines(`diretriz.${fieldIndex}.${itIndex}.id`, { value: it?.id })}
              defaultValue={it?.id}
            />
            <Grid item xs={12}>
              {ReplaceButton(fieldIndex)}
            </Grid>
            <Grid item xs={4}>
              <TextFieldElaboration
                rows={3}
                required
                formName="Diretriz"
                multiline
                defaultValue={texto}
                commentTittle={texto}
                errors={errosGuidelines}
                register={registerGuidelines}
                placeholder="Digite a diretriz"
                label={itIndex === 0 ? it?.label : undefined}
                name={`diretriz.${fieldIndex}.${itIndex}.texto`}
              />
            </Grid>
            <Grid item xs={4} sx={{ mt: -0.5 }}>
              {itIndex != 0 && (
                <div onClick={() => {
                  setOpenDeleteModal(true);
                  setDeleteStrategyId(itIndex)
                  setDeleteField(field)
                  setDeleteFieldId(it?.id)
                  setFieldIndexItem(fieldIndex)
                }} style={{ marginTop: '1rem', display: 'flex', alignItems: 'center' }}>
                  <DeleteIcon />
                </div>
              )}
            </Grid>
            {itIndex === field.length - 1 && (
              <Grid item xs={12} sx={{ mt: -2 }}>
                <div className="add_line_wrapper">
                  <p onClick={() => handleAddLine(field, it, fieldIndex)}>
                    + Adicionar linha
                  </p>
                </div>
              </Grid>
            )}
            <DeleteElaborationDialog
              open={openDeleteModal}
              setOpen={setOpenDeleteModal}
              onDelete={async () => {
                if (deleteFieldId) {
                  const relacionamento = await consultarRelacionamento();
                  const root = JSON.parse(JSON.parse(relacionamento));

                  const newItem = deleteItemById({ root, layerName: 'diretrizes', targetId: deleteFieldId });

                  startRelacionamento(JSON.stringify(newItem));
                  atualizarRelacionamento();

                  DeleteGuideLinesElaborations(deleteFieldId)
                  setDeleteFieldId(0)
                };

                updateGuidelines(fieldIndexItem, fields[fieldIndexItem].filter((_: any, index: any) => index !== deleteStrategyId));
                setOpenDeleteModal(false);
              }}
            />
          </React.Fragment>
        );

        return acc;
      });

      return acc;
    }, []);

    return (
      <>

        {inputs}
      </>
    );
  };

  const ReplaceButton = (index: any) => {
    if (tipoFluxo != 'substituir') return null;
    return (
      <Grid item xs={4}>
        <button className="recicle_content_button" type='button' onClick={() => {
          if (replaceButtonRef?.current?.reuseLen > 0) {
            setReutilizar(true);
            setReuseClickedIndex(index);
          } else {
            setOpenDisclaimer(true);
          }
        }}>
          <p>Reutilizar conteúdo</p>
        </button>
      </Grid>
    );
  };

  const actionButton = () => {
    const opitional = () => {
      if (typeof is_optional == 'string') {
        return is_optional == 'true' ? 'true' : 'false';
      } else {
        return Boolean(is_optional) == true ? 'true' : 'false'
      }
    }

    const optionalV2 = () => {
      if (typeof is_optional == 'string') {
        return is_optional == 'false' || is_optional == undefined;
      } else {
        return Boolean(is_optional) == false || is_optional == undefined;
      }
    }

    return (
      <ActionButtonElaborations disable={disabeSubmit} isOptional={opitional()} layer_indicator={false} onClick={() => {
        if (optionalV2()) {
          PostProgressCode({ code: 'diretrizes', skipped: true }, router.query.id)
          setNavigable(true)

          router.push('/dashboard/elaboracao_planos/' + router.query.id);

          const plan = itemsMenu[findingMenuItemKey(zustandSelectedCardItem)] as Record<string, any>;
          if (plan && plan[selectedItemsMenu?.layer as string]) {
            const planId = plan[selectedItemsMenu?.layer as string]
              .find((item: any) => item.id == selectedItemsMenu?.id).id + 1;
            setDefaultItemMenuId(planId);
          }
        }
      }} />
    )
  }

  const RadioButton = () => {
    const opitional = () => {
      if (typeof is_optional == 'string') {
        return is_optional == 'true' ? 'true' : 'false';
      } else {
        return Boolean(is_optional) == true ? 'true' : 'false'
      }
    }

    return (
      <RadioCheckedElaboration
        required={false}
        name="is_optional"
        disabled={disable}
        errors={errosGuidelines}
        control={controlGuidelines as any}
        defaultValue={opitional()}
        data={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]}
        label="Este elemento é opcional para o tipo de plano selecionado. Deseja preencher a informação?"
      />
    )
  }

  if (loading) {
    return <LoadingBuffer />
  }

  return (
    <form onSubmit={handleSubmitGuidelines(onSubmit)}>
      <Container>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {RadioButton()}
          </Grid>
          {Fields()}
          {is_optional == 'true' && (
            <div className="error_wrapper" style={{ marginLeft: '1rem' }}>
              <span>{errosGuidelines.root?.message}</span>
            </div>

          )}
          <Grid item xs={12}>
            <div style={{ height: '1px', background: '#B4B4B4' }}></div>
          </Grid>
          <Grid item xs={12}>
            {actionButton()}
          </Grid>
          <ReutilizarElaborationDialogV2
            title="Diretriz"
            reuseData={data}
            open={reutilizar}
            ref={replaceButtonRef}
            setOpen={() => setReutilizar(false)}
            setValue={(e) => {
              if (reuseClickedIndex != null) {
                e.map((item: any, index: any) => {
                  const { checked, ...res } = item
                  if (index == 0) {
                    setValueGuidelines(`diretriz.${reuseClickedIndex}.${index}.texto` as any, res.texto)
                    setValueGuidelines(`diretriz.${reuseClickedIndex}.${index}.label` as any, fields[reuseClickedIndex][0]?.label)
                    return;
                  }

                  setValueGuidelines(`diretriz.${reuseClickedIndex}.${index}.texto` as any, res.texto)
                });
              }
            }}
          />
          <DisclaimerReuseElaborationDialog open={openDisclaimer} setOpen={setOpenDisclaimer} />
        </Grid>
      </Container>
    </form>
  )
}
